// Push-Meldungen je Mail-Konto (#774, Mockup Mail 1c #p1). Schalter je Konto
// und — nur bei ungeschützten Konten — was in der Meldung steht. Geschützte
// Konten (bzw. geschützter Posteingang) bekommen immer nur „Neue Nachricht
// (geschützt)“; dafür gibt es hier bewusst keine Auswahl im DOM. Die Vorgabe
// „Absender + Betreff“ ist Dennis' Entscheidung b vom 23.09.2026. Das Gerät
// selbst wird unter Einstellungen › Integrationen registriert, hier nur verlinkt.

import type { MailPushContent } from '@embertime/shared'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link } from '@tanstack/react-router'
import { ChevronLeft, Lock, Smartphone } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ProtectionBadge } from '../components/mail/protection-badge'
import { useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { api, type MailAccount } from '../lib/api'
import { notify } from '../lib/notify'
import { pushEnabled, pushSupported } from '../lib/push'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/mail-push',
  component: MailPushSettings,
})

type PushAccount = Pick<
  MailAccount,
  'id' | 'label' | 'email' | 'protected' | 'pushEnabled' | 'pushContent'
> & { disabledAt?: string | null }

type PushPatch = { enabled?: boolean; content?: MailPushContent }

function MailPushSettings() {
  useMobilePageTitle('Einstellungen · Mail-Konten')
  const qc = useQueryClient()
  const { data, isPending, error } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: () => api.listMailAccounts(),
  })
  // The list endpoint has no ORDER BY and Postgres moves a row on every
  // UPDATE: sort here so the cards do not jump after a toggle.
  const accounts = [...(data?.accounts ?? [])].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.label.localeCompare(b.label),
  )
  // Geschützter oder (noch) unbekannter Posteingang wirkt wie ein geschütztes
  // Konto — dieselbe fail-closed-Regel wie im Server (mail-push.ts). Gleicher
  // Cache-Schlüssel wie die Mail-Seite.
  const folders = useQueries({
    queries: accounts.map((account) => ({
      queryKey: ['mail-folders', account.id],
      queryFn: () => api.listMailFolders(account.id),
      enabled: !account.protected,
    })),
  })

  const save = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PushPatch }) => api.updateMailPush(id, patch),
    // Optimistisch: sofort umschalten, bei Fehler zurück und Toast.
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['mail-accounts'] })
      const previous = qc.getQueryData<{ accounts: MailAccount[] }>(['mail-accounts'])
      qc.setQueryData<{ accounts: MailAccount[] }>(['mail-accounts'], (old) =>
        old
          ? {
              ...old,
              accounts: old.accounts.map((a) =>
                a.id === id
                  ? {
                      ...a,
                      pushEnabled: patch.enabled ?? a.pushEnabled,
                      pushContent: patch.content ?? a.pushContent,
                    }
                  : a,
              ),
            }
          : old,
      )
      return { previous }
    },
    onError: (err: Error, _vars, context) => {
      if (context?.previous) qc.setQueryData(['mail-accounts'], context.previous)
      notify.error(err.message || 'Push-Einstellung nicht gespeichert')
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['mail-accounts'] }),
  })

  return (
    <div className="mx-auto max-w-2xl space-y-3 animate-fade-in">
      <Link
        to="/settings/mail-accounts"
        className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-md px-2 text-sm font-medium text-text-primary hover:bg-bg-overlay"
      >
        <ChevronLeft className="h-5 w-5 text-text-secondary" aria-hidden="true" />
        Einstellungen
      </Link>
      <div>
        <h1 className="text-xl font-semibold">Push-Meldungen</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Neue Mails als Meldung auf dem Handy – je Konto
        </p>
      </div>
      {isPending && <p className="text-sm text-text-tertiary">Konten werden geladen …</p>}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error.message}
        </p>
      )}
      {data && accounts.length === 0 && (
        <p className="text-sm text-text-secondary">
          Noch kein Mail-Konto verbunden.{' '}
          <Link to="/settings/mail-accounts" className="text-accent hover:underline">
            Konto hinzufügen
          </Link>
        </p>
      )}
      {accounts.map((account, index) => {
        const inboxQuery = folders[index]
        const inboxRow = inboxQuery?.data?.folders.find((f) => f.path === 'INBOX')
        const inbox: InboxState = account.protected
          ? 'protected'
          : inboxQuery?.isPending
            ? 'loading'
            : !inboxRow
              ? 'unknown'
              : inboxRow.protected
                ? 'protected'
                : 'open'
        return (
          <MailPushAccountCard
            key={account.id}
            account={account}
            inbox={inbox}
            busy={save.isPending && save.variables?.id === account.id}
            onToggle={(enabled) => save.mutate({ id: account.id, patch: { enabled } })}
            onContent={(content) => save.mutate({ id: account.id, patch: { content } })}
          />
        )
      })}
      <DeviceCard />
      <p className="px-1 text-xs text-text-tertiary">
        Schalter aus = keine Meldung für dieses Konto. Die Mails kommen trotzdem wie gewohnt in den
        Posteingang.
      </p>
    </div>
  )
}

const OPTIONS: Array<{ value: MailPushContent; label: string }> = [
  { value: 'count', label: 'Nur Anzahl' },
  { value: 'sender', label: 'Absender' },
  { value: 'sender_subject', label: 'Absender + Betreff' },
]

/** INBOX state for the card: 'unknown' = not fetched yet or the lookup failed. */
export type InboxState = 'loading' | 'open' | 'protected' | 'unknown'

/** Default of the column (0087) — marked as „Voreingestellt“. */
const DEFAULT_CONTENT: MailPushContent = 'sender_subject'

function initials(label: string): string {
  const letters = label
    .split(/\s+/)
    .filter((word) => /^\p{L}/u.test(word))
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')
  return letters || '@'
}

export function MailPushAccountCard({
  account,
  inbox,
  busy = false,
  onToggle,
  onContent,
}: {
  account: PushAccount
  inbox: InboxState
  busy?: boolean
  onToggle: (enabled: boolean) => void
  onContent: (content: MailPushContent) => void
}) {
  const fixed = account.protected || inbox === 'protected' || inbox === 'unknown'
  const optionsOff = !account.pushEnabled
  const example = (value: MailPushContent) =>
    value === 'count'
      ? `„2 neue Nachrichten · ${account.label}“`
      : value === 'sender'
        ? '„Erika Beispiel“ – Betreff bleibt verborgen'
        : 'auch auf dem Sperrbildschirm lesbar'
  return (
    <article className="overflow-hidden rounded-2xl border border-border-default bg-bg-elevated">
      <div className="flex items-center gap-3 p-4">
        <div
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/15 text-sm font-semibold text-accent"
        >
          {initials(account.label)}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="flex flex-wrap items-center gap-2 font-semibold">
            {account.label}
            {account.protected && <ProtectionBadge />}
          </h2>
          <p className="break-all text-sm text-text-secondary">{account.email}</p>
          {account.disabledAt && (
            <p className="text-xs text-text-tertiary">Konto deaktiviert – keine Meldungen</p>
          )}
        </div>
        <label className="flex min-h-11 min-w-11 shrink-0 cursor-pointer items-center justify-end">
          <input
            type="checkbox"
            role="switch"
            className="peer sr-only"
            aria-label={`Push-Meldungen für ${account.label}`}
            aria-checked={account.pushEnabled}
            checked={account.pushEnabled}
            disabled={busy}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <span
            aria-hidden="true"
            className="relative h-7 w-12 rounded-full border border-border-default bg-bg-overlay transition-colors after:absolute after:left-1 after:top-[3px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:border-accent peer-checked:bg-accent peer-checked:after:translate-x-5 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent"
          />
        </label>
      </div>
      {fixed ? (
        <div className="mx-4 mb-4 flex gap-2.5 rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm text-text-secondary">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <p>
            {account.protected
              ? 'Geschütztes Konto: Die Meldung lautet immer nur'
              : inbox === 'protected'
                ? 'Geschützter Posteingang: Die Meldung lautet immer nur'
                : 'Posteingang noch nicht abgerufen: Bis dahin lautet die Meldung nur'}{' '}
            <b className="font-medium text-text-primary">„Neue Nachricht (geschützt)“</b> – kein
            Absender, kein Betreff.
            {inbox === 'unknown' && !account.protected ? '' : ' Nicht umstellbar.'}
          </p>
        </div>
      ) : inbox === 'loading' ? (
        <p className="border-t border-border-subtle px-4 py-3 text-xs text-text-tertiary">
          Ordner werden geladen …
        </p>
      ) : (
        <fieldset
          className={cn(
            'border-t border-border-subtle px-4 pb-2 pt-3 transition-opacity',
            optionsOff && 'opacity-50',
          )}
        >
          <legend className="float-left w-full pb-1 text-sm font-medium">
            Was steht in der Meldung?
          </legend>
          {OPTIONS.map((option) => (
            <label
              key={option.value}
              className={cn(
                'clear-left flex min-h-11 items-start gap-3 py-2',
                optionsOff ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <input
                type="radio"
                name={`push-content-${account.id}`}
                value={option.value}
                checked={account.pushContent === option.value}
                disabled={optionsOff || busy}
                onChange={() => onContent(option.value)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-accent"
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  {option.label}
                  {option.value === DEFAULT_CONTENT && (
                    <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">
                      Voreingestellt
                    </span>
                  )}
                </span>
                <span className="block text-xs text-text-tertiary">{example(option.value)}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </article>
  )
}

type DeviceState = 'loading' | 'active' | 'off' | 'unsupported'

/** Zustand dieses Browsers (lib/push) — nur Anzeige, Einrichtung unter Integrationen. */
export function DeviceCard({ state: fixedState }: { state?: DeviceState } = {}) {
  const [state, setState] = useState<DeviceState>(fixedState ?? 'loading')
  useEffect(() => {
    if (fixedState) return
    if (!pushSupported()) {
      setState('unsupported')
      return
    }
    let cancelled = false
    pushEnabled()
      .then((on) => {
        if (!cancelled) setState(on ? 'active' : 'off')
      })
      .catch(() => {
        if (!cancelled) setState('off')
      })
    return () => {
      cancelled = true
    }
  }, [fixedState])
  const text =
    state === 'active'
      ? 'Push ist erlaubt'
      : state === 'unsupported'
        ? 'Dieser Browser kann keine Push-Meldungen'
        : state === 'off'
          ? 'Push ist hier aus'
          : 'Wird geprüft …'
  return (
    <Link
      to="/settings/integrations"
      className="flex min-h-11 items-center gap-3 rounded-2xl border border-border-default bg-bg-elevated p-4 hover:bg-bg-overlay"
    >
      <Smartphone className="h-5 w-5 shrink-0 text-text-secondary" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">Dieses Gerät</span>
        <span className="block text-sm text-text-secondary">
          {text} · Einstellungen › Integrationen
        </span>
      </span>
      {state !== 'loading' && (
        <span
          className={cn(
            'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium',
            state === 'active' ? 'bg-success/15 text-success' : 'bg-bg-overlay text-text-tertiary',
          )}
        >
          {state === 'active' ? '✓ aktiv' : 'aus'}
        </span>
      )}
    </Link>
  )
}
